import { Component, ElementRef, EventEmitter, Input, OnInit, Output, signal, ViewChild, Renderer2, AfterViewInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { ListsService } from '../../services/lists.service';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-economic-activity',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule
  ],
  templateUrl: './economic-activity.component.html',
  styleUrl: './economic-activity.component.css'
})
export class EconomicActivityComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('searchInput') searchInput!: ElementRef;
  @ViewChild('subtitleLabel') subtitleLabel!: ElementRef;
  @ViewChild('inbox') inbox!: ElementRef;
  
  @Output() value = new EventEmitter<any>();
  @Input() idDefault = 0;
  @Input() timeoutSeconds = 8;
  
  form: FormGroup;
  label = 'Descripción de la actividad económica';
  searchResults:any = signal([]);
  isLoading = false;
  showResults = false;
  
  // Signals para control de timeout
  offTimeOut = signal(false);
  hasWritten = signal(false);
  hasSelected = signal(false);
  private countdownTimer: any;
  private viewInitialized = signal(false);
  
  private destroy$ = new Subject<void>();
  private searchSubject = new Subject<string>();

  constructor(
    private fb: FormBuilder,
    private _listService: ListsService,
    private renderer: Renderer2
  ) {
    this.form = this.fb.group({
      search: ['', Validators.required]
    });

    this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(searchTerm => {
      this.performSearch(searchTerm);
    });

    // Escuchar cambios del formulario
    this.form.valueChanges.subscribe((value) => {
      this.hasWritten.set(true);
      
      // Si hay un valor válido y se ha seleccionado, limpiar timeout
      if (this.form.valid && value.search && this.hasSelected()) {
        this.clearCountdown();
        this.offTimeOut.set(false);
        this.getStyleEnabled();
      }
    });
  }

  ngOnInit(): void {
    this.startCountdown();

    if(this.idDefault > 0){
      this._listService.findByIdEconomicActivity(this.idDefault).subscribe({
        next: (response:any) => {
          const searchControl = this.form.get('search');
          if (searchControl) {
            searchControl.setValue(response?.data?.name);
            // Si hay valor por defecto, limpiar timeout y marcar como seleccionado
            this.clearCountdown();
            this.offTimeOut.set(false);
            this.hasSelected.set(true);
            this.getStyleEnabled();
          }
        },
        error: (error:any)=>{
          console.error('Error loading default value:', error);
        }
      });
    }
  }

  ngAfterViewInit(): void {
    this.viewInitialized.set(true);
    this.applyInitialStyles();
  }

  private applyInitialStyles() {
    this.getStyleEnabled();
  }

  private safeApplyStyles(callback: () => void) {
    if (this.viewInitialized()) {
      callback();
    }
  }

  // Métodos para el control de timeout y estilos
  startCountdown() {
    this.clearCountdown();
    
    this.countdownTimer = setTimeout(() => {
      
      // Activar error si NO hay nada seleccionado Y el formulario es inválido
      if (!this.hasSelected() || !this.form.valid) {
        this.offTimeOut.set(true);
        this.getStyleError();
      } else {
      }
    }, this.timeoutSeconds * 1000);
  }

  clearCountdown() {
    if (this.countdownTimer) {
      clearTimeout(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  getStyleError(){
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled')
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled')
        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.addClass(this.inbox.nativeElement, 'border-error')
      }
    });
  }
  
  getStyleEnabled(){
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-disabled')
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-error')
        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.addClass(this.inbox.nativeElement, 'border-enabled')
      }
    });
  }

  getStyleTextDisabled(){
    this.safeApplyStyles(() => {
      if (this.subtitleLabel?.nativeElement && this.inbox?.nativeElement) {
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-enabled')
        this.renderer.removeClass(this.subtitleLabel.nativeElement, 'text-label-error');
        this.renderer.removeClass(this.inbox.nativeElement, 'border-error')
        this.renderer.addClass(this.subtitleLabel.nativeElement, 'text-label-disabled');
        this.renderer.addClass(this.inbox.nativeElement, 'border-disabled')
      }
    });
  }

  onSearchInput(): void {
    const searchTerm = this.form.get('search')?.value.trim();
    
    // Cuando el usuario escribe, resetear el estado de selección
    this.hasSelected.set(false);
    
    if (searchTerm.length > 0) {
      this.searchSubject.next(searchTerm);
      this.showResults = true;
    } else {
      this.searchResults.set([]);
      this.showResults = false;
      this.hasSelected.set(false);
    }
  }

  private performSearch(searchTerm: string): void {
    if (searchTerm.length === 0) {
      this.searchResults.set([]);
      this.value.emit({id:''});
      this.hasSelected.set(false);
      return;
    }

    this.isLoading = true;

    this._listService.searchEconomicActivity(searchTerm).subscribe({
      next: (response:any)=> {
        this.isLoading = false;
        this.searchResults.set(response.data);
      },
      error: (error:any) => {
        this.isLoading = false;
        console.error('Error searching economic activity:', error);
      }
    });
  }

  selectResult(result: any): void {
    this.form.get('search')?.setValue(result.name);
    this.showResults = false;
    this.value.emit(result);
    
    // Marcar como seleccionado y limpiar timeout
    this.hasSelected.set(true);
    this.clearCountdown();
    this.offTimeOut.set(false);
    this.getStyleEnabled();
  }

  clearSearch(): void {
    this.form.get('search')?.setValue('');
    this.searchResults.set([]);
    this.showResults = false;
    this.value.emit({id: ''});
    this.hasSelected.set(false);
    
    // Reiniciar el countdown cuando se limpia la búsqueda
    this.startCountdown();
  }

  hasError = () => {
    const shouldShowError = this.offTimeOut() && (!this.hasSelected() || !this.form.valid);
    return shouldShowError;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearCountdown();
  }
}